// Health concerns — the PATIENT-LANGUAGE master that sits in front of the
// specialty catalogue.
//
// A patient does not search for "Otorhinolaryngology"; they tap "Ear, nose &
// throat". This master is that translation layer: one row per everyday
// complaint, pointing at the specialties a booking for it should land on.
//
// Two things make it different from Speciality.jsx, which it otherwise copies
// (table + modal + toggle + soft delete, all behind masters.manage):
//
//  1. The specialty list is ORDERED and the order is meaningful. The first
//     entry is the PRIMARY specialty — the one the patient apps preselect when
//     the concern chip is tapped — so the control has to preserve insertion
//     order and let the admin promote a row, which a checkbox list cannot do.
//     Hence: pick-to-append plus removable chips, first chip badged "Primary".
//  2. A concern must have a TARGET. Either at least one specialty, or a
//     fallback category for the concerns no single specialty owns ("Fever,
//     cold & cough" spans General Medicine, Family Medicine, …). The server
//     enforces this as NEEDS_TARGET; we check it here too so the admin is told
//     before the round trip, not after it.
//
// Global master: no tenant appears anywhere on this page. Every write is
// gated on masters.manage — hidden here, refused by the API regardless.
import React, { useState, useEffect, useMemo } from 'react';
import { motion as Motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Search, X, HeartPulse, Eye, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import Pagination from '../../components/Pagination';
import ErrorPage from '../../components/ErrorPage';
import MasterLabel from '../../components/common/MasterLabel';
import SearchableSelect from '../../components/common/SearchableSelect';
import HealthIconSelect from '../../components/common/HealthIconSelect';
import HealthIcon from '../../icons/health/HealthIcon';
import { useSpecialtyIconStyle } from '../../specialtyIcons';
import { useMasterLocaleTree, resolveMasterLabel } from '../../hooks/useMasterLabel';
import useCanManage from '../../hooks/useCanManage';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import {
    getHealthConcerns,
    createHealthConcern,
    updateHealthConcern,
    deleteHealthConcern,
    toggleHealthConcernStatus,
    getallspecilitylist,
    getSpCategoryList,
} from '../../redux/masterSlice';

const MAX_SEARCH_LENGTH = 50;
const MAX_NAME_LENGTH = 80;
const MAX_DESCRIPTION_LENGTH = 200;
const MAX_SORT_ORDER = 9999;

const EMPTY_FORM = {
    name: '',
    description: '',
    iconKey: null,
    specialtyIds: [],
    categoryId: '',
    sortOrder: 0,
    status: 'active',
};

// The server names the rule that failed; we decide WHICH FIELD wears the
// message. A code that only reached the toast would leave the admin staring
// at a form with no red text on it.
const ERROR_FIELD_BY_CODE = {
    NAME_TAKEN: 'name',
    INVALID_ICON_KEY: 'iconKey',
    INVALID_SPECIALTY: 'specialtyIds',
    NEEDS_TARGET: 'specialtyIds',
    INVALID_CATEGORY: 'categoryId',
};

const HealthConcern = () => {
    const { t } = useTranslation('healthconcern');
    const dispatch = useDispatch();
    const masterTree = useMasterLocaleTree();
    // Write-side controls hide without the manage key — the API refuses them
    // anyway (hooks/useCanManage.js).
    const canManage = useCanManage('masters.manage');
    // The one platform-wide icon style — never a per-row choice.
    const iconStyle = useSpecialtyIconStyle();

    const {
        healthConcerns,
        healthConcernPagination,
        healthConcernLoading,
        healthConcernCreateLoading,
        healthConcernUpdateLoading,
        healthConcernDeleteLoading,
        healthConcernToggleLoading,
        specialties,
        spCategoryList,
        error,
    } = useSelector((state) => state.master);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [showModal, setShowModal] = useState(false);
    const [editingConcern, setEditingConcern] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});

    const [viewingConcern, setViewingConcern] = useState(null);

    // Debounce the search box; a keystroke must not be a request.
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // The two option lists the form needs. Open reads, cheap, fetched once.
    useEffect(() => {
        dispatch(getallspecilitylist({}));
        dispatch(getSpCategoryList());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getHealthConcerns({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab,
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, activeTab]);

    const rows = useMemo(() => healthConcerns || [], [healthConcerns]);

    // Translated option lists, built from ONE tree read (see useMasterLabel:
    // the hook cannot be called per row).
    const specialtyOptions = useMemo(
        () => (specialties || [])
            .filter((s) => s && s.specialtyId)
            .map((s) => ({
                value: s.specialtyId,
                label: resolveMasterLabel(masterTree, 'masterSpecialty', s.specialtyId, 'name', s.name),
            })),
        [specialties, masterTree]
    );

    const categoryOptions = useMemo(
        () => (spCategoryList || []).map((c) => ({
            value: c.spcategoryId,
            label: resolveMasterLabel(masterTree, 'masterSpecialtyCategory', c.spcategoryId, 'spcategoryName', c.spcategoryName),
        })),
        [spCategoryList, masterTree]
    );

    const specialtyNameById = useMemo(() => {
        const map = {};
        specialtyOptions.forEach((o) => { map[o.value] = o.label; });
        return map;
    }, [specialtyOptions]);

    const categoryNameById = useMemo(() => {
        const map = {};
        categoryOptions.forEach((o) => { map[o.value] = o.label; });
        return map;
    }, [categoryOptions]);

    // A row's `specialties[]` comes resolved from the API; fall back to the
    // option list when a build is ahead of the response shape.
    const specialtyLabelsFor = (concern) => {
        const resolved = Array.isArray(concern?.specialties) ? concern.specialties : null;
        if (resolved && resolved.length) {
            return resolved.map((s) => ({
                id: s.specialtyId,
                label: resolveMasterLabel(masterTree, 'masterSpecialty', s.specialtyId, 'name', s.name),
            }));
        }
        return (concern?.specialtyIds || []).map((id) => ({
            id,
            label: specialtyNameById[id] || '—',
        }));
    };

    const refreshList = (page = currentPage) => {
        dispatch(getHealthConcerns({
            page,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab,
        }));
    };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value.replace(/[<>]/g, '').substring(0, MAX_SEARCH_LENGTH));
    };

    const clearFieldError = (field) => {
        setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processed = value;
        if (name === 'name') {
            processed = value.replace(/[<>]/g, '').substring(0, MAX_NAME_LENGTH);
        } else if (name === 'description') {
            processed = value.replace(/[<>]/g, '').substring(0, MAX_DESCRIPTION_LENGTH);
        } else if (name === 'sortOrder') {
            // Digits only, and never wider than the column. An empty box is
            // allowed while typing and reads as 0 on save.
            processed = value.replace(/[^0-9]/g, '').substring(0, 4);
        }
        setFormData((prev) => ({ ...prev, [name]: processed }));
        clearFieldError(name);
    };

    // --- the ordered specialty picker -------------------------------------
    const addSpecialty = (specialtyId) => {
        if (!specialtyId) return;
        setFormData((prev) => (
            prev.specialtyIds.includes(specialtyId)
                ? prev
                : { ...prev, specialtyIds: [...prev.specialtyIds, specialtyId] }
        ));
        clearFieldError('specialtyIds');
    };

    const removeSpecialty = (specialtyId) => {
        setFormData((prev) => ({
            ...prev,
            specialtyIds: prev.specialtyIds.filter((id) => id !== specialtyId),
        }));
    };

    // Promote to position 0. Without this, changing which specialty is primary
    // would mean clearing the whole list and re-picking it in order.
    const makePrimary = (specialtyId) => {
        setFormData((prev) => ({
            ...prev,
            specialtyIds: [specialtyId, ...prev.specialtyIds.filter((id) => id !== specialtyId)],
        }));
    };

    const unpickedSpecialtyOptions = useMemo(
        () => specialtyOptions.filter((o) => !formData.specialtyIds.includes(o.value)),
        [specialtyOptions, formData.specialtyIds]
    );

    // --- validation --------------------------------------------------------
    const validateForm = () => {
        const next = {};
        const name = formData.name.trim();

        if (!name) {
            next.name = t('healthconcern.name_required', 'Concern name is required');
        } else if (name.length < 2) {
            next.name = t('healthconcern.name_min_length', 'Concern name must be at least 2 characters');
        } else if (name.length > MAX_NAME_LENGTH) {
            next.name = t('healthconcern.name_max_length', 'Concern name cannot exceed 80 characters');
        }

        if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
            next.description = t('healthconcern.description_max_length', 'Description cannot exceed 200 characters');
        }

        // NEEDS_TARGET, checked before the round trip. A concern nothing can
        // be booked against is a dead chip on the patient home screen.
        if (formData.specialtyIds.length === 0 && !formData.categoryId) {
            next.specialtyIds = t('healthconcern.needs_target', 'Pick at least one specialty, or a fallback category.');
        }

        const sortOrder = Number(formData.sortOrder || 0);
        if (Number.isNaN(sortOrder) || sortOrder < 0 || sortOrder > MAX_SORT_ORDER) {
            next.sortOrder = t('healthconcern.sort_order_invalid', 'Sort order must be a number between 0 and 9999');
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    // --- modal lifecycle ---------------------------------------------------
    const handleAdd = () => {
        setEditingConcern(null);
        // A new row lands at the end of the list by default.
        setFormData({ ...EMPTY_FORM, sortOrder: String((healthConcernPagination?.total || 0) + 1) });
        setErrors({});
        setShowModal(true);
    };

    const handleEdit = (concern) => {
        setEditingConcern(concern);
        setFormData({
            name: concern.name || '',
            description: concern.description || '',
            iconKey: concern.iconKey || null,
            specialtyIds: Array.isArray(concern.specialtyIds) ? [...concern.specialtyIds] : [],
            categoryId: concern.categoryId || '',
            sortOrder: String(concern.sortOrder ?? 0),
            status: concern.status || 'active',
        });
        setErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingConcern(null);
        setFormData(EMPTY_FORM);
        setErrors({});
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        const payload = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            iconKey: formData.iconKey || null,
            specialtyIds: formData.specialtyIds,
            categoryId: formData.categoryId || null,
            sortOrder: Number(formData.sortOrder || 0),
            status: formData.status,
        };

        try {
            if (editingConcern) {
                await dispatch(updateHealthConcern({ id: editingConcern.concernId, ...payload })).unwrap();
                toast.success(t('healthconcern.updated_success', 'Health concern has been updated successfully.'));
                refreshList();
            } else {
                await dispatch(createHealthConcern(payload)).unwrap();
                toast.success(t('healthconcern.created_success', 'Health concern has been created successfully.'));
                setCurrentPage(1);
                refreshList(1);
            }
            handleCloseModal();
        } catch (err) {
            // The server's message may be an {en, fr} object — unwrap it.
            const fallback = editingConcern
                ? t('healthconcern.update_failed', 'Failed to update health concern.')
                : t('healthconcern.create_failed', 'Failed to create health concern.');
            const message = extractErrorMessage(err?.error, fallback);
            const field = ERROR_FIELD_BY_CODE[err?.code];
            if (field) setErrors((prev) => ({ ...prev, [field]: message }));
            toast.warn(message);
        }
    };

    const handleDelete = async (concern) => {
        const result = await Swal.fire({
            title: t('healthconcern.delete_title', 'Delete health concern?'),
            text: t('healthconcern.delete_text', { name: concern.name, defaultValue: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.' }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('healthconcern.yes_delete', 'Yes, delete it!'),
            cancelButtonText: t('healthconcern.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            await dispatch(deleteHealthConcern(concern.concernId)).unwrap();
            toast.success(t('healthconcern.deleted_success', 'Health concern has been deleted successfully.'));
            refreshList();
        } catch (err) {
            toast.warn(extractErrorMessage(err?.error, t('healthconcern.delete_failed', 'Failed to delete health concern.')));
        }
    };

    const handleStatusToggle = async (concern) => {
        const newStatus = concern.status === 'active' ? 'inactive' : 'active';
        const result = await Swal.fire({
            title: t('healthconcern.status_change_title', 'Confirm status change'),
            text: t('healthconcern.status_change_text', {
                action: newStatus === 'active' ? t('healthconcern.activate', 'activate') : t('healthconcern.deactivate', 'deactivate'),
                name: concern.name,
                defaultValue: 'Are you sure you want to {{action}} "{{name}}"?',
            }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('healthconcern.confirm', 'Confirm'),
            cancelButtonText: t('healthconcern.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            await dispatch(toggleHealthConcernStatus({ id: concern.concernId, status: newStatus })).unwrap();
            toast.success(newStatus === 'active'
                ? t('healthconcern.activated_success', 'Health concern activated successfully.')
                : t('healthconcern.deactivated_success', 'Health concern deactivated successfully.'));
            refreshList();
        } catch (err) {
            toast.warn(extractErrorMessage(err?.error, t('healthconcern.status_update_failed', 'Failed to update health concern status.')));
        }
    };

    // Only blank the page when the FIRST fetch failed and there is nothing to
    // show. A failed toggle belongs in a toast, not on a full-page error.
    if (error && rows.length === 0 && !healthConcernLoading) {
        return <ErrorPage error={error} pageName={t('healthconcern.page_name', 'Health Concerns')} />;
    }

    const saving = healthConcernCreateLoading || healthConcernUpdateLoading;

    const renderSpecialtyChips = (concern) => {
        const labels = specialtyLabelsFor(concern);
        if (labels.length === 0) {
            return (
                <span className="text-xs text-gray-400" data-testid={`health-concern-master-row-no-specialties-${concern.concernId}`}>
                    {t('healthconcern.no_specialties', 'None')}
                </span>
            );
        }
        return (
            <div className="flex flex-wrap gap-1">
                {labels.map((s, index) => (
                    <span
                        key={s.id}
                        // The first chip IS the primary specialty — shown in
                        // solid teal so the ordering is visible at a glance.
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${index === 0 ? 'bg-[#4ABDBD] text-white' : 'bg-[#4ABDBD]/10 text-[#2D8A8A]'}`}
                        title={index === 0 ? t('healthconcern.primary', 'Primary') : undefined}
                        data-testid={`health-concern-master-row-specialty-${concern.concernId}-${s.id}`}
                    >
                        {s.label}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className="w-full" data-testid="health-concern-master-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4" data-testid="health-concern-master-header">
                    <div>
                        <h1 className="text-2xl sm:text-3xl pb-1 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="health-concern-master-title">
                            {t('healthconcern.title', 'Health Concerns')}
                        </h1>
                        <p className="text-sm text-gray-500 ml-2 max-w-3xl" data-testid="health-concern-master-intro">
                            {t('healthconcern.intro', 'What a patient calls the problem, mapped to the specialties that treat it. These are the "Consult by concern" chips on the patient home screen.')}
                        </p>
                    </div>
                    {canManage && (
                        <button
                            onClick={handleAdd}
                            className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer shrink-0"
                            data-testid="health-concern-master-add-button"
                        >
                            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                            {t('healthconcern.add_concern', 'Add Concern')}
                        </button>
                    )}
                </div>

                {/* Tabs + search */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="health-concern-master-tab-search-section">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2" data-testid="health-concern-master-tabs">
                        {['active', 'inactive'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                                className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                                data-testid={`health-concern-master-tab-${tab}`}
                            >
                                {activeTab === tab && (
                                    <Motion.div
                                        layoutId="activeHealthConcernStatusTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20">
                                    {tab === 'active' ? t('healthconcern.active', 'Active') : t('healthconcern.inactive', 'Inactive')}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="relative group w-full sm:w-1/3" data-testid="health-concern-master-search-container">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={22} />
                        <input
                            type="text"
                            placeholder={t('healthconcern.search_placeholder', 'Search concerns by name...')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={MAX_SEARCH_LENGTH}
                            className="w-full pl-12 pr-4 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400"
                            data-testid="health-concern-master-search-input"
                        />
                    </div>
                </div>

                {healthConcernLoading ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="health-concern-master-loading-container">
                        <div className="flex flex-col items-center gap-4">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin" data-testid="health-concern-master-loading-spinner"></div>
                            <p className="text-gray-500 text-sm">{t('healthconcern.loading', 'Loading health concerns...')}</p>
                        </div>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center py-16 sm:py-20" data-testid="health-concern-master-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <HeartPulse size={48} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" data-testid="health-concern-master-empty-title">
                            {debouncedSearchQuery ? t('healthconcern.none_found', 'No health concerns found') : t('healthconcern.none_yet', 'No health concerns yet')}
                        </h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto">
                            {debouncedSearchQuery
                                ? t('healthconcern.try_adjusting_search', 'Try adjusting your search terms to find what you’re looking for.')
                                : t('healthconcern.create_first', 'Get started by creating your first health concern.')}
                        </p>
                        {canManage && !debouncedSearchQuery && activeTab === 'active' && (
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                                data-testid="health-concern-master-empty-create-button"
                            >
                                <Plus size={20} />
                                {t('healthconcern.create_first_button', 'Create your first concern')}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200 mb-4" data-testid="health-concern-master-table-container">
                            <table className="w-full border-collapse rounded-md overflow-hidden" data-testid="health-concern-master-table">
                                <thead>
                                    <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="health-concern-master-header-sort-order">{t('healthconcern.sort_order', 'Order')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="health-concern-master-header-icon">{t('healthconcern.icon', 'Icon')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="health-concern-master-header-name">{t('healthconcern.name', 'Concern')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="health-concern-master-header-specialties">{t('healthconcern.specialties', 'Specialties')}</th>
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="health-concern-master-header-category">{t('healthconcern.fallback_category', 'Fallback category')}</th>
                                        <th className="p-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="health-concern-master-header-status">{t('healthconcern.status', 'Status')}</th>
                                        <th className="p-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="health-concern-master-header-actions">{t('healthconcern.actions', 'Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((concern) => {
                                        const isInactive = concern.status === 'inactive';
                                        return (
                                            <tr
                                                key={concern.concernId}
                                                className={`border-b border-gray-100/50 transition-all duration-300 ${isInactive ? 'bg-gray-100/50' : 'hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80'}`}
                                                data-testid={`health-concern-master-row-${concern.concernId}`}
                                            >
                                                <td className={`p-3 text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`} data-testid={`health-concern-master-row-sort-order-${concern.concernId}`}>
                                                    {concern.sortOrder ?? 0}
                                                </td>
                                                <td className="p-3">
                                                    {concern.iconKey ? (
                                                        <span
                                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${isInactive ? 'bg-gray-100 text-gray-400' : 'bg-[#4ABDBD]/10 text-[#2D8A8A]'}`}
                                                            title={concern.iconKey}
                                                            data-testid={`health-concern-master-row-icon-${concern.concernId}`}
                                                        >
                                                            <HealthIcon iconKey={concern.iconKey} variant={iconStyle} size={18} />
                                                        </span>
                                                    ) : (
                                                        <span className="text-[10px] text-gray-400" data-testid={`health-concern-master-row-no-icon-${concern.concernId}`}>
                                                            {t('healthconcern.no_icon', 'No icon')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={`p-3 text-xs font-semibold ${isInactive ? 'text-gray-400' : 'text-gray-800'}`} data-testid={`health-concern-master-row-name-${concern.concernId}`}>
                                                    <MasterLabel type="healthConcern" id={concern.concernId} field="name" fallback={concern.name} />
                                                </td>
                                                <td className="p-3" data-testid={`health-concern-master-row-specialties-${concern.concernId}`}>
                                                    {renderSpecialtyChips(concern)}
                                                </td>
                                                <td className={`p-3 text-xs ${isInactive ? 'text-gray-400' : 'text-gray-600'}`} data-testid={`health-concern-master-row-category-${concern.concernId}`}>
                                                    {concern.categoryId
                                                        ? (categoryNameById[concern.categoryId] || '—')
                                                        : '-'}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="flex justify-center">
                                                        <div
                                                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive ? 'bg-gray-300' : 'bg-[#4ABDBD]'} ${healthConcernToggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                            onClick={() => canManage && !healthConcernToggleLoading && handleStatusToggle(concern)}
                                                            data-testid={`health-concern-master-toggle-${concern.concernId}`}
                                                        >
                                                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive ? 'translate-x-1' : 'translate-x-[18px]'}`}></span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => setViewingConcern(concern)}
                                                            className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105"
                                                            title={t('healthconcern.view_details', 'View Details')}
                                                            data-testid={`health-concern-master-view-button-${concern.concernId}`}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                        {canManage && (
                                                            <button
                                                                onClick={() => handleEdit(concern)}
                                                                className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105"
                                                                title={t('healthconcern.edit', 'Edit')}
                                                                data-testid={`health-concern-master-edit-button-${concern.concernId}`}
                                                            >
                                                                <Edit2 className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                        {canManage && (
                                                            <button
                                                                onClick={() => handleDelete(concern)}
                                                                disabled={healthConcernDeleteLoading}
                                                                className="text-red-500 hover:text-white p-2 rounded-lg bg-red-50 hover:bg-red-500 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                title={t('healthconcern.delete', 'Delete')}
                                                                data-testid={`health-concern-master-delete-button-${concern.concernId}`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards — the same row, stacked. */}
                        <div className="block sm:hidden space-y-4 mb-4" data-testid="health-concern-master-cards-container">
                            {rows.map((concern) => {
                                const isInactive = concern.status === 'inactive';
                                return (
                                    <div
                                        key={concern.concernId}
                                        className={`p-4 rounded-xl shadow-md transition-all duration-300 ${isInactive ? 'bg-gray-100/50' : 'bg-gray-50 hover:shadow-lg'}`}
                                        data-testid={`health-concern-master-card-${concern.concernId}`}
                                    >
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {concern.iconKey && (
                                                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${isInactive ? 'bg-gray-100 text-gray-400' : 'bg-[#4ABDBD]/10 text-[#2D8A8A]'}`}>
                                                        <HealthIcon iconKey={concern.iconKey} variant={iconStyle} size={18} />
                                                    </span>
                                                )}
                                                <span className={`text-sm font-bold truncate ${isInactive ? 'text-gray-400' : 'text-gray-800'}`}>
                                                    <MasterLabel type="healthConcern" id={concern.concernId} field="name" fallback={concern.name} />
                                                </span>
                                            </div>
                                            <span className={`text-xs font-bold shrink-0 ${isInactive ? 'text-gray-400' : 'text-[#4ABDBD]'}`}>#{concern.sortOrder ?? 0}</span>
                                        </div>
                                        <div className="mb-3">{renderSpecialtyChips(concern)}</div>
                                        {concern.categoryId && (
                                            <div className="text-xs text-gray-600 mb-3">
                                                <span className="font-bold">{t('healthconcern.fallback_category', 'Fallback category')}:</span>{' '}
                                                {categoryNameById[concern.categoryId] || '—'}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                            <div
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive ? 'bg-gray-300' : 'bg-[#4ABDBD]'} ${healthConcernToggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                onClick={() => canManage && !healthConcernToggleLoading && handleStatusToggle(concern)}
                                                data-testid={`health-concern-master-card-toggle-${concern.concernId}`}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive ? 'translate-x-1' : 'translate-x-[18px]'}`}></span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setViewingConcern(concern)}
                                                    className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-blue-50 hover:bg-[#4ABDBD] shadow-sm cursor-pointer transition-all duration-300"
                                                    data-testid={`health-concern-master-card-view-button-${concern.concernId}`}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                {canManage && (
                                                    <button
                                                        onClick={() => handleEdit(concern)}
                                                        className="text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-blue-50 hover:bg-[#4ABDBD] shadow-sm cursor-pointer transition-all duration-300"
                                                        data-testid={`health-concern-master-card-edit-button-${concern.concernId}`}
                                                    >
                                                        <Edit2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {canManage && (
                                                    <button
                                                        onClick={() => handleDelete(concern)}
                                                        disabled={healthConcernDeleteLoading}
                                                        className="text-red-500 hover:text-white p-2 rounded-lg bg-red-50 hover:bg-red-500 shadow-sm cursor-pointer transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        data-testid={`health-concern-master-card-delete-button-${concern.concernId}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {healthConcernPagination?.total > 0 && (
                            <Pagination
                                totalItems={healthConcernPagination.total}
                                currentPage={healthConcernPagination.page || currentPage}
                                itemsPerPage={healthConcernPagination.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </>
                )}

                {/* Create / edit modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="health-concern-master-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="health-concern-master-modal-content">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="health-concern-master-modal-title">
                                    {editingConcern
                                        ? t('healthconcern.edit_concern', 'Edit Health Concern')
                                        : t('healthconcern.add_new_concern', 'Add New Health Concern')}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="health-concern-master-modal-close-button"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-6 space-y-6" data-testid="health-concern-master-form">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('healthconcern.name_label', 'Concern name')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        maxLength={MAX_NAME_LENGTH}
                                        placeholder={t('healthconcern.name_placeholder', 'e.g. Fever, cold & cough')}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        data-testid="health-concern-master-name-input"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="health-concern-master-name-error">{errors.name}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('healthconcern.description_label', 'Short description')} <span className="text-gray-400">{t('healthconcern.optional', '(optional)')}</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={3}
                                        maxLength={MAX_DESCRIPTION_LENGTH}
                                        placeholder={t('healthconcern.description_placeholder', 'What this concern covers, in the words a patient would use...')}
                                        className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent resize-none transition-all ${errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                        data-testid="health-concern-master-description-input"
                                    />
                                    {errors.description && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="health-concern-master-description-error">{errors.description}</p>
                                    )}
                                </div>

                                {/* Icon */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('healthconcern.icon_label', 'Icon')} <span className="text-gray-400">{t('healthconcern.optional', '(optional)')}</span>
                                    </label>
                                    <HealthIconSelect
                                        testId="health-concern-master-icon-select"
                                        value={formData.iconKey}
                                        onChange={(key) => {
                                            setFormData((prev) => ({ ...prev, iconKey: key }));
                                            clearFieldError('iconKey');
                                        }}
                                        placeholder={t('healthconcern.icon_placeholder', 'Choose an icon for the concern chip')}
                                        error={Boolean(errors.iconKey)}
                                    />
                                    {errors.iconKey ? (
                                        <p className="mt-1 text-sm text-red-500" data-testid="health-concern-master-icon-error">{errors.iconKey}</p>
                                    ) : (
                                        <p className="mt-1 text-xs text-gray-400">
                                            {t('healthconcern.icon_hint', 'The drawing ships with every app — only the key is stored here.')}
                                        </p>
                                    )}
                                </div>

                                {/* Specialties — ordered, first is primary */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('healthconcern.specialties_label', 'Specialties')}
                                    </label>
                                    <p className="text-xs text-gray-400 mb-2" data-testid="health-concern-master-specialties-hint">
                                        {t('healthconcern.specialties_hint', 'In priority order — the first one is the primary specialty a booking starts from.')}
                                    </p>
                                    {formData.specialtyIds.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-2" data-testid="health-concern-master-specialty-chips">
                                            {formData.specialtyIds.map((id, index) => (
                                                <span
                                                    key={id}
                                                    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium ${index === 0 ? 'bg-[#4ABDBD] text-white' : 'bg-[#4ABDBD]/10 text-[#2D8A8A]'}`}
                                                    data-testid={`health-concern-master-specialty-chip-${id}`}
                                                >
                                                    {index === 0 && (
                                                        <span className="uppercase text-[9px] tracking-wider opacity-90">
                                                            {t('healthconcern.primary', 'Primary')}
                                                        </span>
                                                    )}
                                                    {specialtyNameById[id] || '—'}
                                                    {index > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => makePrimary(id)}
                                                            title={t('healthconcern.make_primary', 'Make primary')}
                                                            className="p-0.5 rounded-full hover:bg-[#4ABDBD]/20 cursor-pointer"
                                                            data-testid={`health-concern-master-specialty-primary-${id}`}
                                                        >
                                                            <ArrowUp className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSpecialty(id)}
                                                        title={t('healthconcern.remove', 'Remove')}
                                                        className={`p-0.5 rounded-full cursor-pointer ${index === 0 ? 'hover:bg-white/20' : 'hover:bg-[#4ABDBD]/20'}`}
                                                        data-testid={`health-concern-master-specialty-remove-${id}`}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {/* Pick-to-append. The control's own value is
                                        always '' — it is an "add" box, not a
                                        field holding a selection. */}
                                    <SearchableSelect
                                        testId="health-concern-master-specialty-select"
                                        variant="form"
                                        value=""
                                        onChange={addSpecialty}
                                        options={unpickedSpecialtyOptions}
                                        placeholder={t('healthconcern.add_specialty', 'Add a specialty…')}
                                        searchPlaceholder={t('healthconcern.search', 'Search…')}
                                        className={errors.specialtyIds ? 'border-red-300' : ''}
                                    />
                                    {errors.specialtyIds && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="health-concern-master-specialties-error">{errors.specialtyIds}</p>
                                    )}
                                </div>

                                {/* Fallback category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('healthconcern.category_label', 'Fallback category')}
                                    </label>
                                    <p className="text-xs text-gray-400 mb-2">
                                        {t('healthconcern.category_hint', 'Used when no single specialty fits. One of the two is required.')}
                                    </p>
                                    <SearchableSelect
                                        testId="health-concern-master-category-select"
                                        variant="form"
                                        value={formData.categoryId}
                                        onChange={(v) => {
                                            setFormData((prev) => ({ ...prev, categoryId: v }));
                                            clearFieldError('categoryId');
                                            clearFieldError('specialtyIds');
                                        }}
                                        options={categoryOptions}
                                        placeholder={t('healthconcern.select_category', 'No fallback category')}
                                        searchPlaceholder={t('healthconcern.search', 'Search…')}
                                        className={errors.categoryId ? 'border-red-300' : ''}
                                    />
                                    {errors.categoryId && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="health-concern-master-category-error">{errors.categoryId}</p>
                                    )}
                                </div>

                                {/* Sort order + status */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('healthconcern.sort_order_label', 'Sort order')}
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            name="sortOrder"
                                            value={formData.sortOrder}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                            className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.sortOrder ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                            data-testid="health-concern-master-sort-order-input"
                                        />
                                        {errors.sortOrder ? (
                                            <p className="mt-1 text-sm text-red-500" data-testid="health-concern-master-sort-order-error">{errors.sortOrder}</p>
                                        ) : (
                                            <p className="mt-1 text-xs text-gray-400">{t('healthconcern.sort_order_hint', 'Lowest first on the patient home screen.')}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('healthconcern.status_label', 'Status')}
                                        </label>
                                        <SearchableSelect
                                            testId="health-concern-master-status-select"
                                            variant="form"
                                            value={formData.status}
                                            onChange={(v) => setFormData((prev) => ({ ...prev, status: v || 'active' }))}
                                            options={[
                                                { value: 'active', label: t('healthconcern.active', 'Active') },
                                                { value: 'inactive', label: t('healthconcern.inactive', 'Inactive') },
                                            ]}
                                            placeholder={t('healthconcern.active', 'Active')}
                                            searchPlaceholder={t('healthconcern.search', 'Search…')}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="health-concern-master-modal-cancel-button"
                                >
                                    {t('healthconcern.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="health-concern-master-modal-save-button"
                                >
                                    {saving && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>}
                                    {saving
                                        ? (editingConcern ? t('healthconcern.updating', 'Updating...') : t('healthconcern.creating', 'Creating...'))
                                        : (editingConcern ? t('healthconcern.update', 'Update') : t('healthconcern.create', 'Create'))}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View modal */}
                {viewingConcern && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="health-concern-master-view-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="health-concern-master-view-modal-title">
                                    {t('healthconcern.details', 'Health Concern Details')}
                                </h2>
                                <button
                                    onClick={() => setViewingConcern(null)}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="health-concern-master-view-modal-close-button"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-1">{t('healthconcern.name', 'Concern')}</p>
                                    <p className="text-gray-800 font-semibold" data-testid="health-concern-master-view-name">{viewingConcern.name}</p>
                                </div>
                                {viewingConcern.description && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-700 mb-1">{t('healthconcern.description_label', 'Short description')}</p>
                                        <p className="text-gray-600 text-sm">{viewingConcern.description}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-1">{t('healthconcern.specialties', 'Specialties')}</p>
                                    {renderSpecialtyChips(viewingConcern)}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-1">{t('healthconcern.fallback_category', 'Fallback category')}</p>
                                    <p className="text-gray-600 text-sm">
                                        {viewingConcern.categoryId
                                            ? (categoryNameById[viewingConcern.categoryId] || '—')
                                            : '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-1">{t('healthconcern.sort_order', 'Order')}</p>
                                    <p className="text-gray-600 text-sm">{viewingConcern.sortOrder ?? 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HealthConcern;
